import React, { useState, useEffect } from 'react';
import { generatePromptSuggestion, createPromptVersion, getCurrentUser } from '../services/api';

const SuggestionModal = ({ 
  isOpen, 
  onClose, 
  promptId, 
  testRunId, 
  logId,
  originalPrompt, 
  onSuggestionAccepted 
}) => {
  const [suggestion, setSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);

  useEffect(() => {
    if (isOpen && (testRunId || logId) && promptId) {
      generateSuggestion();
    }
  }, [isOpen, testRunId, logId, promptId]);


  const generateSuggestion = async () => {
    setIsLoading(true);
    setStreamingContent('');
    setError(null);
    setSuggestion(null);

    try {
      // Use streaming for real-time experience
      const response = await generatePromptSuggestion(promptId, testRunId, true, logId);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let fullContent = '';
      let hasReceivedCompleteData = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'chunk') {
                fullContent += data.content;
                setStreamingContent(fullContent);
              } else if (data.type === 'complete') {
                setSuggestion(data);
                setIsLoading(false);
                hasReceivedCompleteData = true;
              } else if (data.type === 'error') {
                setError(data.error);
                setIsLoading(false);
                return;
              }
            } catch (e) {
              // Ignore JSON parse errors for incomplete chunks
            }
          }
        }
      }

      // If we didn't receive a complete event but have streaming content, parse it manually
      if (!hasReceivedCompleteData && fullContent) {
        
        
        // Parse the accumulated content
        const analysisMatch = fullContent.match(/ANALYSIS:\s*([\s\S]*?)(?=IMPROVED PROMPT:|$)/);
        const promptMatch = fullContent.match(/IMPROVED PROMPT:\s*([\s\S]*?)(?=KEY CHANGES:|$)/);
        const changesMatch = fullContent.match(/KEY CHANGES:\s*([\s\S]*?)$/);

        const parsedSuggestion = {
          analysis: analysisMatch ? analysisMatch[1].trim() : '',
          improvedPrompt: promptMatch ? promptMatch[1].trim() : fullContent,
          keyChanges: changesMatch ? changesMatch[1].trim() : '',
          fullResponse: fullContent,
          success: true,
          type: 'complete'
        };

        setSuggestion(parsedSuggestion);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Streaming failed, trying non-streaming approach:', error);
      
      // Fallback to non-streaming approach
      try {
        const suggestion = await generatePromptSuggestion(promptId, testRunId, false, logId);
        setSuggestion(suggestion);
        setIsLoading(false);
      } catch (fallbackError) {
        console.error('Non-streaming approach also failed:', fallbackError);
        setError(`Failed to generate suggestion: ${fallbackError.message}`);
        setIsLoading(false);
      }
    }
  };

  const handleAccept = async () => {
    if (!suggestion || !suggestion.improvedPrompt) {
      console.error('No suggestion available to accept');
      return;
    }

    setIsAccepting(true);
    try {
      const changeMessage = `AI-suggested improvement: ${
        suggestion.keyChanges?.split('\n')[0]?.substring(0, 100) || 
        'Improved based on test failure analysis'
      }`;
      
      const currentUser = await getCurrentUser();
      
      await createPromptVersion(promptId, {
        content: suggestion.improvedPrompt.trim(),
        changeMessage,
        authorId: currentUser.id
      });

      if (onSuggestionAccepted) {
        onSuggestionAccepted(suggestion);
      }
      
      onClose();
    } catch (error) {
      console.error('Error accepting suggestion:', error);
      setError('Failed to create new version with suggestion. Please try again.');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleReject = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="bg-white rounded-2xl shadow-strong max-w-6xl w-full max-h-[95vh] overflow-hidden animate-scale-in">
        {/* Header */}
        <div className="gradient-header border-b border-gray-100 px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-celadon-100 rounded-xl">
                <svg className="h-6 w-6 text-celadon-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">AI Prompt Suggestion</h2>
                <p className="text-gray-600 mt-1">Intelligent analysis and improvement recommendations</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost p-2 h-auto w-auto rounded-xl"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col h-[calc(95vh-140px)]">
          <div className="flex-1 flex overflow-hidden">
            {/* Left Side - Analysis and Info */}
            <div className="w-1/3 border-r border-gray-100 bg-gray-50 overflow-y-auto custom-scrollbar">
              <div className="p-6 space-y-6">
                {isLoading && (
                  <div className="space-y-4">
                    <div className="flex items-center text-celadon-600">
                      <div className="spinner h-5 w-5 mr-3"></div>
                      <span className="font-medium">Analyzing prompt...</span>
                    </div>
                    {streamingContent && (
                      <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-soft">
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                          {streamingContent}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-soft">
                    <div className="flex items-start space-x-3">
                      <svg className="h-5 w-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 mb-2">Error Generating Suggestion</h4>
                        <p className="text-gray-600 text-sm mb-4">{error}</p>
                        <button
                          onClick={generateSuggestion}
                          className="btn-secondary btn-sm"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {suggestion && (
                  <div className="space-y-6">
                    {suggestion.analysis && (
                      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-soft">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <svg className="h-5 w-5 text-celadon-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Analysis
                        </h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {suggestion.analysis}
                        </p>
                      </div>
                    )}

                    {suggestion.keyChanges && (
                      <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-soft">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                          <svg className="h-5 w-5 text-celadon-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Key Changes
                        </h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {suggestion.keyChanges}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Diff Viewer */}
            <div className="flex-1 flex flex-col">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <h4 className="font-semibold text-gray-900 flex items-center">
                  <svg className="h-5 w-5 text-celadon-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                  </svg>
                  Prompt Comparison
                </h4>
                <p className="text-sm text-gray-600 mt-1">Original vs. AI Suggested</p>
              </div>

              {suggestion && (
                <div className="flex-1 overflow-hidden">
                  <DiffViewer 
                    original={originalPrompt}
                    suggested={suggestion.improvedPrompt}
                  />
                </div>
              )}

              {isLoading && (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="spinner h-12 w-12 mx-auto mb-6"></div>
                    <p className="text-gray-600 font-medium">Generating AI suggestion...</p>
                    <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 flex-shrink-0">
            <div className="flex items-center justify-between">
              {/* Error display */}
              {error && !isLoading && (
                <div className="flex-1 mr-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-soft">
                    <div className="flex items-center">
                      <svg className="h-4 w-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-gray-800 text-sm font-medium">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleReject}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAccept}
                  disabled={isAccepting || !suggestion || !suggestion.improvedPrompt || isLoading}
                  className="btn-primary"
                >
                  {isAccepting ? (
                    <>
                      <div className="spinner h-4 w-4 mr-2"></div>
                      Creating Version...
                    </>
                  ) : (
                    suggestion && suggestion.improvedPrompt ? 'Accept & Create Version' : 'Waiting for Suggestion...'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Diff Viewer Component
const DiffViewer = ({ original, suggested }) => {
  const [viewMode, setViewMode] = useState('split'); // 'split' or 'unified'

  const diffLines = React.useMemo(() => {
    if (!original || !suggested) return [];
    
    const originalLines = original.split('\n');
    const suggestedLines = suggested.split('\n');
    
    // Simple line-by-line diff implementation
    const maxLines = Math.max(originalLines.length, suggestedLines.length);
    const diff = [];
    
    for (let i = 0; i < maxLines; i++) {
      const originalLine = originalLines[i] || '';
      const suggestedLine = suggestedLines[i] || '';
      
      if (originalLine === suggestedLine) {
        diff.push({ type: 'equal', original: originalLine, suggested: suggestedLine, lineNumber: i + 1 });
      } else if (originalLine && !suggestedLine) {
        diff.push({ type: 'delete', original: originalLine, suggested: '', lineNumber: i + 1 });
      } else if (!originalLine && suggestedLine) {
        diff.push({ type: 'add', original: '', suggested: suggestedLine, lineNumber: i + 1 });
      } else {
        diff.push({ type: 'change', original: originalLine, suggested: suggestedLine, lineNumber: i + 1 });
      }
    }
    
    return diff;
  }, [original, suggested]);

  return (
    <div className="h-full flex flex-col">
      {/* View Mode Toggle */}
      <div className="p-4 border-b border-gray-100 bg-gray-50">
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode('split')}
            className={`px-3 py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
              viewMode === 'split' 
                ? 'bg-celadon-500 text-white shadow-soft' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Split View
          </button>
          <button
            onClick={() => setViewMode('unified')}
            className={`px-3 py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
              viewMode === 'unified' 
                ? 'bg-celadon-500 text-white shadow-soft' 
                : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            Unified View
          </button>
        </div>
      </div>

      {/* Diff Content */}
      <div className="flex-1 overflow-auto custom-scrollbar">
        {viewMode === 'split' ? (
          <div className="flex h-full">
            {/* Original */}
            <div className="w-1/2 border-r border-gray-200">
              <div className="bg-gray-100 p-3 border-b border-gray-200 text-sm font-semibold text-gray-800 flex items-center">
                <svg className="h-4 w-4 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Original Prompt
              </div>
              <div className="p-6">
                <pre className="text-sm whitespace-pre-wrap text-gray-900 font-mono leading-relaxed">
                  {original}
                </pre>
              </div>
            </div>
            {/* Suggested */}
            <div className="w-1/2">
              <div className="bg-celadon-100 p-3 border-b border-gray-200 text-sm font-semibold text-celadon-800 flex items-center">
                <svg className="h-4 w-4 text-celadon-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Suggested Prompt
              </div>
              <div className="p-6">
                <pre className="text-sm whitespace-pre-wrap text-gray-900 font-mono leading-relaxed">
                  {suggested}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6">
            {diffLines.map((line, index) => (
              <div
                key={index}
                className={`flex font-mono text-sm border-l-4 pl-4 py-2 rounded-r-lg mb-1 ${
                  line.type === 'delete' 
                    ? 'bg-gray-50 border-gray-400' 
                    : line.type === 'add'
                    ? 'bg-celadon-50 border-celadon-500'
                    : line.type === 'change'
                    ? 'bg-yellow-50 border-yellow-500'
                    : 'border-gray-200'
                }`}
              >
                <span className="w-12 text-gray-500 mr-4 select-none font-medium">
                  {line.lineNumber}
                </span>
                <span className="flex-1">
                  {line.type === 'delete' && (
                    <span className="text-gray-700">- {line.original}</span>
                  )}
                  {line.type === 'add' && (
                    <span className="text-celadon-800">+ {line.suggested}</span>
                  )}
                  {line.type === 'change' && (
                    <>
                      <div className="text-gray-700">- {line.original}</div>
                      <div className="text-celadon-800">+ {line.suggested}</div>
                    </>
                  )}
                  {line.type === 'equal' && (
                    <span className="text-gray-800">{line.original}</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuggestionModal; 